import { memo, type PropsWithChildren, type ReactElement } from 'react';
import styled from 'styled-components';

import colors from '@wecasa/common/src/utils/colors';
import { NOOP } from '@wecasa/common/src/utils/noop';
import { toRem } from '@wecasa/common/src/utils/styles';

import Card from '../card/card';
import Checkbox, { type CheckboxProps } from '../checkbox/checkbox';
import type { WithClassNameProps } from '../types';

const Label = styled.label`
  display: block;
  width: 100%;
  height: 100%;
`;

const StyledCard = styled(Card)`
  width: 100%;
  overflow: hidden;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled(Checkbox)`
  margin: ${toRem(20)} 0 ${toRem(20)} ${toRem(15)};
`;

// we sometimes need to show a close icon on the up-down corner
const Content = styled.div`
  position: relative;
`;

const Footer = styled.div`
  display: flex;
  box-sizing: border-box;
  background-color: ${colors.white100};
`;

export type CheckboxCardProps = Pick<CheckboxProps, 'id' | 'value' | 'name' | 'onChange' | 'checked'> &
  WithClassNameProps &
  PropsWithChildren<{
    role?: string;
    footer?: ReactElement | null;
  }>;

const CheckboxCard = ({
  children,
  id,
  value,
  name,
  role = 'checkbox',
  onChange,
  checked = false,
  footer = null,
  className,
}: CheckboxCardProps) => (
  <Label htmlFor={id} className={className}>
    <StyledCard selected={checked} onPress={NOOP} role={role} color={colors.cream100}>
      <Container>
        <Content>{children}</Content>
        <Footer>
          <StyledCheckbox checked={checked} id={id} value={value} name={name} onChange={onChange} />
          {footer}
        </Footer>
      </Container>
    </StyledCard>
  </Label>
);

export default memo(CheckboxCard);
